import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import './MDTaskTable.css';
import TaskService from '../../../../../../../AppSettings/services/TaskService';
import DataBar from './DataBar/DataBar';
import ActionItemDetailsPanel from '@frontend/Tasks/Tasks/Components/ActionItemDetailsPanel';
import ConfirmationModal from '../../../../../../../Components/ConfirmationModal/ConfirmationModal';
import CreateTaskModal from './CreateTask/CreateTaskModal';
import { TaskByEmployee } from '@frontend/Models/Tasks/TaskModels';
import SecondaryTable from '@frontend/Components/SecondaryTable/SecondaryTable';
import { Column } from '@frontend/Models/Table/TableModels';
import CompactFilterBar from './CompactFilterBar/CompactFilterBar';
import useActiveEmployeeId from '../../../../../../../AppSettings/hooks/useActiveEmployeeId';

interface Task extends TaskByEmployee {}

const MDTasksTable: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const employeeId = useActiveEmployeeId();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showMyTasks, setShowMyTasks] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState<string | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [, setActiveTable] = useState<string>('');

  // Fetch tasks on component mount and when toggle changes
  useEffect(() => {
    const fetchTasks = async () => {
      if (!matterId) return;
      try {
        let data: Task[];
        if (showMyTasks && employeeId) {
          data = await TaskService.getTasksByMatterAndEmployee(matterId, employeeId);
        } else {
          data = await TaskService.getTasksByMatterId(matterId);
        }
        setTasks(data);
        // Reset filters when tasks change
        setTypeFilter('all');
        setScopeFilter('all');
        setAssigneeFilter('all');
        setSearchTerm('');
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    fetchTasks();
  }, [matterId, showMyTasks, employeeId]);

  // Apply all filters (status, type, scope, assignee, search)
  useEffect(() => {
    let filtered = [...tasks];

    // Status filter: null = default (Pending + In Progress only), 'Total' = show all, else filter by status
    if (statusFilter === null) {
      filtered = filtered.filter(
        (task) => task.status === 'Pending' || task.status === 'In Progress'
      );
    } else if (statusFilter !== 'Total') {
      filtered = filtered.filter((task) => task.status === statusFilter);
    }

    // Apply type and scope filters
    // If scope is set (Internal/External), use that; otherwise use type filter
    if (scopeFilter !== 'all') {
      // Scope filter takes precedence (Internal/External)
      filtered = filtered.filter((task) => task.task_type === scopeFilter);
    } else if (typeFilter !== 'all') {
      // If scope is not set, use type filter
      filtered = filtered.filter((task) => task.task_type === typeFilter);
    }

    // Apply assignee filter
    if (assigneeFilter !== 'all') {
      filtered = filtered.filter((task) => task.assignedto === assigneeFilter);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((task) => {
        return (
          task.task_title?.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.assignedto?.toLowerCase().includes(searchLower) ||
          task.task_type?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredTasks(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [statusFilter, tasks, typeFilter, scopeFilter, assigneeFilter, searchTerm]);

  const handleStatusFilter = (status: string) => {
    if (statusFilter === status) {
      setStatusFilter(null); // Back to default view (Pending + In Progress)
    } else {
      setStatusFilter(status);
    }
  };

  const openSidebar = (task: Task) => {
    setSelectedTask(task);
    setIsSidebarOpen(true);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedTask(null);
  };

  const refetchTasks = async () => {
    if (!matterId) return;
    try {
      let data: Task[];
      if (showMyTasks && employeeId) {
        data = await TaskService.getTasksByMatterAndEmployee(matterId, employeeId);
      } else {
        data = await TaskService.getTasksByMatterId(matterId);
      }
      setTasks(data);
      setFilteredTasks(data);
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  const handleTaskEdit = (updatedTask?: TaskByEmployee) => {
    if (updatedTask && selectedTask && updatedTask.taskid === selectedTask.taskid) {
      setSelectedTask(updatedTask as Task);
    }
    refetchTasks();
  };

  const handleTaskDelete = () => {
    refetchTasks();
    closeSidebar();
  };

  const openModal = (taskId: string, taskTitle: string) => {
    setSelectedTaskId(taskId);
    setSelectedTaskTitle(taskTitle);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTaskId(null);
    setSelectedTaskTitle(null);
  };

  const confirmDelete = async () => {
    if (!selectedTaskId) return;
    try {
      const result = await TaskService.deleteTask(selectedTaskId);
      alert(result.message);
      // Refresh the tasks list after successful deletion
      if (matterId) {
        let updatedTasks: Task[];
        if (showMyTasks && employeeId) {
          updatedTasks = await TaskService.getTasksByMatterAndEmployee(matterId, employeeId);
        } else {
          updatedTasks = await TaskService.getTasksByMatterId(matterId);
        }
        setTasks(updatedTasks);
        setFilteredTasks(updatedTasks);
      }
      closeModal();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  // Calculate pagination
  const indexOfLastTask = currentPage * pageSize;
  const indexOfFirstTask = indexOfLastTask - pageSize;
  const currentTasks = filteredTasks.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(filteredTasks.length / pageSize);

  const getStatusClass = (status?: string | null) => {
    if (!status) return 'status-pill default';
    const normalized = status.toLowerCase();
    if (normalized.includes('progress')) return 'status-pill in-progress';
    if (normalized.includes('complete')) return 'status-pill complete';
    if (normalized.includes('pending')) return 'status-pill pending';
    if (normalized.includes('overdue')) return 'status-pill overdue';
    return 'status-pill default';
  };

  const formatDueDate = (value?: string | null) => {
    if (!value) {
      return { label: '—', subLabel: '' };
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return { label: value, subLabel: '' };
    }

    const now = new Date();
    const diffMs = parsed.getTime() - now.getTime();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

    const label = parsed.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (Number.isNaN(days)) {
      return { label, subLabel: '' };
    }

    if (days === 0) {
      return { label, subLabel: 'Due today' };
    }

    if (days > 0) {
      return {
        label,
        subLabel: `${days} day${days === 1 ? '' : 's'} left`,
      };
    }

    return {
      label,
      subLabel: `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`,
    };
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Calculate status counts
  const statusCounts = {
    total: tasks.length,
    pending: tasks.filter((t) => t.status === 'Pending').length,
    inProgress: tasks.filter((t) => t.status === 'In Progress').length,
    completed: tasks.filter((t) => t.status === 'Completed').length,
  };

  // Define table columns
  const columns: Column<Task>[] = [
    {
      header: 'Task',
      render: (row: Task) => (
        <button
          className="secondary-Sub-Table-Link"
          onClick={() => openSidebar(row)}
        >
          {row.task_title}
        </button>
      ),
    },
    {
      header: 'Status',
      render: (row: Task) => (
        <span className={getStatusClass(row.status)}>{row.status || '—'}</span>
      ),
    },
    {
      header: 'Assigned To',
      icon: faUser,
      accessor: 'assignedto',
    },
    {
      header: 'Due Date',
      render: (row: Task) => {
        const { label, subLabel } = formatDueDate(row.duedate);
        return (
          <div className="due-date-cell">
            <span className="due-date-label">{label}</span>
            {subLabel && <span className="due-date-sub">{subLabel}</span>}
          </div>
        );
      },
    },
    {
      header: '',
      render: (row: Task) => (
        <button
          className="delete-button"
          onClick={() => openModal(row.taskid, row.task_title)}
          aria-label="Delete Task"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      ),
    },
  ];

  return (
    <div className="MD-tasks-wrapper">
      <div className="MD-tasks-unified-container">
        <CompactFilterBar
          showMyTasks={showMyTasks}
          onToggleTasks={setShowMyTasks}
          currentStatusFilter={statusFilter ?? 'Total'}
          onStatusFilter={handleStatusFilter}
          statusCounts={statusCounts}
          tasks={tasks}
          searchTerm={searchTerm}
          onSearch={setSearchTerm}
          areaFilter={typeFilter}
          onAreaFilterChange={setTypeFilter}
          scopeFilter={scopeFilter}
          onScopeFilterChange={setScopeFilter}
          priorityFilter={assigneeFilter}
          onPriorityFilterChange={setAssigneeFilter}
          onAddTask={() => setIsCreateTaskModalOpen(true)}
        />
        <DataBar
          setActiveTable={setActiveTable}
          onStatusFilter={handleStatusFilter}
          currentStatusFilter={statusFilter}
        />
        <SecondaryTable
          columns={columns}
          data={currentTasks}
          rowKey="taskid"
          emptyMessage="No tasks found"
          pagination={{
            currPage: currentPage,
            pageLimit: totalPages,
            pageSize: pageSize,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
      </div>
      <ActionItemDetailsPanel
        task={selectedTask}
        onClose={closeSidebar}
        isOpen={isSidebarOpen}
        onEdit={handleTaskEdit}
        onDelete={handleTaskDelete}
      />
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDelete}
        message={`Are you sure you want to delete the task "${selectedTaskTitle}"?`}
      />
      {matterId && (
        <CreateTaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={async () => {
            setIsCreateTaskModalOpen(false);
            // Refresh tasks after creating
            try {
              let data: Task[];
              if (showMyTasks && employeeId) {
                data = await TaskService.getTasksByMatterAndEmployee(matterId, employeeId);
              } else {
                data = await TaskService.getTasksByMatterId(matterId);
              }
              setTasks(data);
              setFilteredTasks(data);
            } catch (error) {
              console.error('Error refreshing tasks:', error);
            }
          }}
          matterId={matterId}
        />
      )}
    </div>
  );
};

export default MDTasksTable;
